import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  FileText,
  Filter,
  X,
  Save,
  AlertCircle,
  ArrowLeft,
  Settings,
  ChevronDown,
  ChevronUp,
  Info,
  CheckCircle,
  Clock,
  Calendar,
  FileType,
  Target,
  Building2,
  Tag,
  Layers
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { 
  httpsCallable 
} from 'firebase/functions';
import { functions } from '../config/firebase';
import { recordMatchesSearch } from '../utils/textSanitize';

const DocumentosObrigatoriosPage = () => {
  const { user } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterFrequencia, setFilterFrequencia] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: '',
    frequencia: '',
    validade_dias: '',
    formato_aceito: [],
    aplicacao: {
      todas_industrias: true,
      industrias: [],
      categorias: [],
      subcategorias: []
    },
    obrigatorio_homologacao: true
  });
  const [errors, setErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Opções da base global
  const [industrias, setIndustrias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  
  // ✅ NOVO: Estados para seleção hierárquica dinâmica
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState([]);
  const [subcategoriasDisponiveis, setSubcategoriasDisponiveis] = useState([]);

  // Opções para os campos
  const tipos = [
    { value: 'licenca', label: 'Licença' },
    { value: 'certificado', label: 'Certificado' },
    { value: 'ficha', label: 'Ficha Técnica' },
    { value: 'laudo', label: 'Laudo' },
    { value: 'contrato', label: 'Contrato' },
    { value: 'outros', label: 'Outros' }
  ];

  const frequencias = [
    { value: 'anual', label: 'Anual' },
    { value: 'semestral', label: 'Semestral' },
    { value: 'trimestral', label: 'Trimestral' },
    { value: 'mensal', label: 'Mensal' },
    { value: 'unico', label: 'Único' }
  ];

  const formatos = [
    'PDF', 'JPG', 'JPEG', 'PNG', 'DOC', 'DOCX', 'XLS', 'XLSX'
  ];

  // Carregar documentos
  useEffect(() => {
    if (user) {
      loadDocumentos();
      loadStats();
      loadOptions();
    }
  }, [user]);

  // Carregar opções da base global
  const loadOptions = async () => {
    setOptionsLoading(true);
    try {
      const getOptions = httpsCallable(functions, 'getProdutosRegulaMaisOptionsPublic');
      const result = await getOptions();
      if (result.data.success) {
        setIndustrias(result.data.data.industrias);
        setCategorias(result.data.data.categorias);
        setSubcategorias(result.data.data.subcategorias);
      }
    } catch (error) {
      console.error('Erro ao carregar opções da base global:', error);
    } finally {
      setOptionsLoading(false);
    }
  };

  // ✅ NOVA FUNÇÃO: Carregar categorias disponíveis para uma indústria específica
  const loadCategoriasPorIndustria = async (industria) => {
    if (!industria) {
      setCategoriasDisponiveis([]);
      setSubcategoriasDisponiveis([]);
      return;
    }

    try {
      const getCategorias = httpsCallable(functions, 'getCategoriasPorIndustria');
      const result = await getCategorias({ industria });
      
      if (result.data.success) {
        setCategoriasDisponiveis(result.data.data.categorias);
        // Limpar subcategorias quando mudar indústria
        setSubcategoriasDisponiveis([]);
        setFormData(prev => ({
          ...prev,
          aplicacao: {
            ...prev.aplicacao,
            categorias: [],
            subcategorias: []
          }
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar categorias por indústria:', error);
      setCategoriasDisponiveis([]);
    }
  };

  // ✅ NOVA FUNÇÃO: Carregar subcategorias disponíveis para uma categoria específica
  const loadSubcategoriasPorCategoria = async (industria, categoria) => {
    if (!industria || !categoria) {
      setSubcategoriasDisponiveis([]);
      return;
    }

    try {
      const getSubcategorias = httpsCallable(functions, 'getSubcategoriasPorCategoria');
      const result = await getSubcategorias({ industria, categoria });
      
      if (result.data.success) {
        setSubcategoriasDisponiveis(result.data.data.subcategorias);
        // Limpar subcategorias selecionadas quando mudar categoria
        setFormData(prev => ({
          ...prev,
          aplicacao: {
            ...prev.aplicacao,
            subcategorias: []
          }
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar subcategorias por categoria:', error);
      setSubcategoriasDisponiveis([]);
    }
  };

  const loadDocumentos = async () => {
    try {
      setLoading(true);
      const listDocumentos = httpsCallable(functions, 'listDocumentosObrigatorios');
      const result = await listDocumentos();
      
      if (result.data.success) {
        setDocumentos(result.data.data.documentos);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const getStats = httpsCallable(functions, 'getDocumentosObrigatoriosStats');
      const result = await getStats();
      
      if (result.data.success) {
        setStats(result.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Filtrar documentos
  const documentosFiltrados = documentos.filter(doc => {
    const matchSearch = recordMatchesSearch(doc, searchTerm, ['nome', 'descricao', 'tipo']);
    const matchStatus = !filterStatus || doc.status === filterStatus;
    const matchTipo = !filterTipo || doc.tipo === filterTipo;
    const matchFrequencia = !filterFrequencia || doc.frequencia === filterFrequencia;
    
    return matchSearch && matchStatus && matchTipo && matchFrequencia;
  });

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório';
    if (!formData.descricao.trim()) newErrors.descricao = 'Descrição é obrigatória';
    if (!formData.tipo) newErrors.tipo = 'Tipo é obrigatório';
    if (!formData.frequencia) newErrors.frequencia = 'Frequência é obrigatória';
    if (formData.frequencia !== 'unico' && !formData.validade_dias) {
      newErrors.validade_dias = 'Validade em dias é obrigatória para documentos periódicos';
    }
    if (formData.frequencia === 'unico' && formData.validade_dias) {
      newErrors.validade_dias = 'Documentos únicos não podem ter validade em dias';
    }
    if (formData.formato_aceito.length === 0) newErrors.formato_aceito = 'Pelo menos um formato deve ser selecionado';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar documento
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const docData = {
        ...formData,
        validade_dias: formData.frequencia === 'unico' ? null : parseInt(formData.validade_dias)
      };

      if (editingDoc) {
        const updateDoc = httpsCallable(functions, 'updateDocumentoObrigatorio');
        await updateDoc({ id: editingDoc.id, ...docData });
      } else {
        const createDoc = httpsCallable(functions, 'createDocumentoObrigatorio');
        await createDoc(docData);
      }

      handleCloseForm();
      loadDocumentos();
      loadStats();
    } catch (error) {
      console.error('Erro ao salvar documento:', error);
      
      // ✅ MELHORAR: Tratamento de erros mais amigável
      let errorMessage = 'Erro ao salvar documento. Tente novamente.';
      
      if (error.code === 'functions/invalid-argument') {
        // Extrair mensagem de erro do backend
        const errorDetails = error.message || '';
        
        // Mapear erros técnicos para mensagens amigáveis
        if (errorDetails.includes('descricao deve ter pelo menos 10 caracteres')) {
          errorMessage = 'A descrição deve ter pelo menos 10 caracteres. Por favor, forneça mais detalhes sobre o documento.';
        } else if (errorDetails.includes('nome deve ter pelo menos 3 caracteres')) {
          errorMessage = 'O nome do documento deve ter pelo menos 3 caracteres.';
        } else if (errorDetails.includes('formato_aceito')) {
          errorMessage = 'Selecione pelo menos um formato de arquivo aceito.';
        } else if (errorDetails.includes('validade_dias')) {
          errorMessage = 'Documentos periódicos devem ter validade em dias especificada.';
        } else if (errorDetails.includes('Documentos únicos não podem ter validade')) {
          errorMessage = 'Documentos únicos não podem ter validade em dias.';
        } else if (errorDetails.includes('Dados inválidos')) {
          // Extrair apenas a parte útil da mensagem
          const cleanMessage = errorDetails.replace('Dados inválidos: ', '');
          errorMessage = `Por favor, verifique os seguintes campos: ${cleanMessage}`;
        } else {
          errorMessage = errorDetails || errorMessage;
        }
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'Você não tem permissão para realizar esta ação.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Recurso não encontrado. Por favor, recarregue a página e tente novamente.';
      }
      
      alert(errorMessage);
    }
  };

  // Deletar documento
  const handleDelete = async (id) => {
    try {
      const deleteDoc = httpsCallable(functions, 'deleteDocumentoObrigatorio');
      await deleteDoc({ id });
      setDeleteConfirm(null);
      loadDocumentos();
      loadStats();
    } catch (error) {
      console.error('Erro ao deletar documento:', error);
      alert('Erro ao deletar documento. Tente novamente.');
    }
  };

  // Abrir formulário para edição
  const handleEdit = (documento) => {
    setEditingDoc(documento);
    setFormData({
      nome: documento.nome || '',
      descricao: documento.descricao || '',
      tipo: documento.tipo || '',
      frequencia: documento.frequencia || '',
      validade_dias: documento.validade_dias?.toString() || '',
      formato_aceito: documento.formato_aceito || [],
      aplicacao: documento.aplicacao || {
        todas_industrias: true,
        industrias: [],
        categorias: [],
        subcategorias: []
      },
      obrigatorio_homologacao: documento.obrigatorio_homologacao !== undefined ? documento.obrigatorio_homologacao : true
    });
    
    // ✅ NOVO: Carregar opções dinâmicas se não aplicar a todas as indústrias
    if (documento.aplicacao && !documento.aplicacao.todas_industrias) {
      if (documento.aplicacao.industrias?.length > 0) {
        loadCategoriasPorIndustria(documento.aplicacao.industrias[0]);
        if (documento.aplicacao.categorias?.length > 0) {
          loadSubcategoriasPorCategoria(documento.aplicacao.industrias[0], documento.aplicacao.categorias[0]);
        }
      }
    }
    
    setShowForm(true);
  };

  // Fechar formulário
  const handleCloseForm = () => {
    setShowForm(false);
    setEditingDoc(null);
    setFormData({
      nome: '',
      descricao: '',
      tipo: '',
      frequencia: '',
      validade_dias: '',
      formato_aceito: [],
      aplicacao: {
        todas_industrias: true,
        industrias: [],
        categorias: [],
        subcategorias: []
      },
      obrigatorio_homologacao: true
    });
    setErrors({});
    
    // ✅ NOVO: Limpar estados dinâmicos
    setCategoriasDisponiveis([]);
    setSubcategoriasDisponiveis([]);
  };


  // Toggle formato aceito
  const toggleFormato = (formato) => {
    setFormData(prev => ({
      ...prev,
      formato_aceito: prev.formato_aceito.includes(formato)
        ? prev.formato_aceito.filter(f => f !== formato)
        : [...prev.formato_aceito, formato]
    }));
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText className="text-blue-600" />
                Documentos Obrigatórios
              </h1>
              <p className="text-gray-600 mt-2">
                Gerencie os documentos obrigatórios para homologação de fornecedores
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Novo Documento
            </button>
          </div>
        </div>

        {/* Lista de documentos */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Documentos ({documentosFiltrados.length})
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Frequência
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documentosFiltrados.map((documento) => (
                  <tr key={documento.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{documento.nome}</div>
                        <div className="text-sm text-gray-500">{documento.descricao}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {tipos.find(t => t.value === documento.tipo)?.label || documento.tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {frequencias.find(f => f.value === documento.frequencia)?.label || documento.frequencia}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        documento.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {documento.status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(documento)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(documento)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {documentosFiltrados.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum documento encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                Comece criando seu primeiro documento obrigatório.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de formulário para criar/editar documento */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingDoc ? 'Editar Documento' : 'Novo Documento Obrigatório'}
              </h3>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
              {/* Informações básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome do Documento *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => handleInputChange('nome', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.nome ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Licença Sanitária"
                  />
                  {errors.nome && <p className="mt-1 text-sm text-red-600">{errors.nome}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo *
                  </label>
                  <select
                    value={formData.tipo}
                    onChange={(e) => handleInputChange('tipo', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.tipo ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione o tipo</option>
                    {tipos.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                  {errors.tipo && <p className="mt-1 text-sm text-red-600">{errors.tipo}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição *
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => handleInputChange('descricao', e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.descricao ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Descreva o documento e sua finalidade"
                />
                {errors.descricao && <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>}
              </div>

              {/* Frequência e validade */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequência *
                  </label>
                  <select
                    value={formData.frequencia}
                    onChange={(e) => handleInputChange('frequencia', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.frequencia ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione a frequência</option>
                    {frequencias.map(freq => (
                      <option key={freq.value} value={freq.value}>{freq.label}</option>
                    ))}
                  </select>
                  {errors.frequencia && <p className="mt-1 text-sm text-red-600">{errors.frequencia}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validade em Dias {formData.frequencia !== 'unico' ? '*' : '(não aplicável)'}
                  </label>
                  <input
                    type="number"
                    value={formData.validade_dias}
                    onChange={(e) => handleInputChange('validade_dias', e.target.value)}
                    disabled={formData.frequencia === 'unico'}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.validade_dias ? 'border-red-500' : 'border-gray-300'
                    } ${formData.frequencia === 'unico' ? 'bg-gray-100' : ''}`}
                    placeholder="Ex: 365 para anual"
                  />
                  {errors.validade_dias && <p className="mt-1 text-sm text-red-600">{errors.validade_dias}</p>}
                </div>
              </div>

              {/* Formatos aceitos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formatos Aceitos *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {formatos.map(formato => (
                    <label key={formato} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.formato_aceito.includes(formato)}
                        onChange={() => toggleFormato(formato)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{formato}</span>
                    </label>
                  ))}
                </div>
                {errors.formato_aceito && <p className="mt-1 text-sm text-red-600">{errors.formato_aceito}</p>}
              </div>

              {/* Aplicação hierárquica */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Aplicação por Indústria/Categoria/Subcategoria</h4>
                
                <div className="space-y-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.aplicacao.todas_industrias}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        aplicacao: {
                          ...prev.aplicacao,
                          todas_industrias: e.target.checked,
                          industrias: e.target.checked ? [] : prev.aplicacao.industrias,
                          categorias: e.target.checked ? [] : prev.aplicacao.categorias,
                          subcategorias: e.target.checked ? [] : prev.aplicacao.subcategorias
                        }
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Aplicar a todas as indústrias</span>
                  </label>

                                     {!formData.aplicacao.todas_industrias && (
                     <div className="ml-6 space-y-4">
                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-2">
                           Indústrias Específicas
                         </label>
                         <select
                           multiple
                           value={formData.aplicacao.industrias || []}
                           onChange={(e) => {
                             const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                             setFormData(prev => ({
                               ...prev,
                               aplicacao: {
                                 ...prev.aplicacao,
                                 industrias: selectedOptions
                               }
                             }));
                             
                             // ✅ NOVO: Carregar categorias disponíveis para a primeira indústria selecionada
                             if (selectedOptions.length > 0) {
                               loadCategoriasPorIndustria(selectedOptions[0]);
                             } else {
                               setCategoriasDisponiveis([]);
                               setSubcategoriasDisponiveis([]);
                             }
                           }}
                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                         >
                           {industrias.map(industria => (
                             <option key={industria} value={industria}>
                               {industria}
                             </option>
                           ))}
                         </select>
                         <p className="text-xs text-gray-500 mt-1">
                           Pressione Ctrl (ou Cmd no Mac) para selecionar múltiplas opções
                         </p>
                       </div>

                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-2">
                           Categorias Específicas
                         </label>
                         <select
                           multiple
                           value={formData.aplicacao.categorias || []}
                           onChange={(e) => {
                             const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                             
                             // ✅ NOVO: Carregar subcategorias disponíveis ANTES de atualizar o estado
                             if (selectedOptions.length > 0) {
                               const industriaAtual = formData.aplicacao.industrias?.[0];
                               if (industriaAtual) {
                                 loadSubcategoriasPorCategoria(industriaAtual, selectedOptions[0]);
                               }
                             } else {
                               setSubcategoriasDisponiveis([]);
                             }
                             
                             // Atualizar o estado DEPOIS de carregar as subcategorias
                             setFormData(prev => ({
                               ...prev,
                               aplicacao: {
                                 ...prev.aplicacao,
                                 categorias: selectedOptions
                               }
                             }));
                           }}
                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                           disabled={categoriasDisponiveis.length === 0}
                         >
                           {categoriasDisponiveis.length === 0 ? (
                             <option value="">Selecione uma indústria primeiro</option>
                           ) : (
                             categoriasDisponiveis.map(categoria => (
                               <option key={categoria} value={categoria}>
                                 {categoria}
                               </option>
                             ))
                           )}
                         </select>
                         <p className="text-xs text-gray-500 mt-1">
                           {categoriasDisponiveis.length === 0 
                             ? 'Selecione uma indústria para ver as categorias disponíveis'
                             : 'Pressione Ctrl (ou Cmd no Mac) para selecionar múltiplas opções'
                           }
                         </p>
                       </div>

                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-2">
                           Subcategorias Específicas
                         </label>
                         <select
                           multiple
                           value={formData.aplicacao.subcategorias || []}
                           onChange={(e) => {
                             const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                             setFormData(prev => ({
                               ...prev,
                               aplicacao: {
                                 ...prev.aplicacao,
                                 subcategorias: selectedOptions
                               }
                             }));
                           }}
                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                           disabled={subcategoriasDisponiveis.length === 0}
                         >
                           {subcategoriasDisponiveis.length === 0 ? (
                             <option value="">Selecione uma categoria primeiro</option>
                           ) : (
                             subcategoriasDisponiveis.map(subcategoria => (
                               <option key={subcategoria} value={subcategoria}>
                                 {subcategoria}
                               </option>
                             ))
                           )}
                         </select>
                         <p className="text-xs text-gray-500 mt-1">
                           {subcategoriasDisponiveis.length === 0 
                             ? 'Selecione uma categoria para ver as subcategorias disponíveis'
                             : 'Pressione Ctrl (ou Cmd no Mac) para selecionar múltiplas opções'
                           }
                         </p>
                       </div>
                     </div>
                   )}
                </div>
              </div>

              {/* Configurações de homologação */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Configurações de Homologação</h4>
                
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.obrigatorio_homologacao}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        obrigatorio_homologacao: e.target.checked
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Obrigatório para homologação</span>
                  </label>
                </div>
              </div>

              {/* Botões de ação */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingDoc ? 'Atualizar' : 'Criar'} Documento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Confirmar Exclusão
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Tem certeza que deseja excluir o documento "{deleteConfirm.nome}"? Esta ação não pode ser desfeita.
              </p>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosObrigatoriosPage;
